import {
  EConnectorApprovalLevel,
  EToolApprovalStance,
  type TActionStanceValue,
} from "../types/connector-approval";

export const CONNECTOR_APPROVAL_QUERY_KEY = "connector-approval-settings";

export const CONNECTOR_APPROVAL_CATALOG_QUERY_KEY =
  "connector-approval-catalog";

// NOTE: NSAI-5354 — the member's connected MCP servers plus each server's live tool list.
// Kept out of CONNECTOR_APPROVAL_CATALOG_QUERY_KEY on purpose: that catalog is static
// metadata cached forever, while this one performs a handshake per server and can change
// whenever a server is added, removed, or goes down.
export const MCP_APPROVAL_TARGETS_QUERY_KEY = "mcp-approval-targets";

export const ORG_CONNECTOR_APPROVAL_QUERY_KEY = "org-connector-approval-policy";

export const EFFECTIVE_ORG_CONNECTOR_APPROVAL_QUERY_KEY =
  "effective-org-connector-approval-policy";

// NOTE: NSAI-4976 — per-connector permission-change history. Invalidated with
// the effective floor whenever a level change lands, so an open history card
// picks up the new entry.
export const CONNECTOR_APPROVAL_POLICY_CHANGES_QUERY_KEY =
  "connector-approval-policy-changes";

// NOTE: NSAI-4090 / NSAI-4976 — every cache a CONNECTOR_PERMISSION_CHANGED
// notification makes stale: the effective org floor, the per-user settings it
// clamps, and the permission-change history. Shared so the web and mobile
// socket handlers refresh the same set.
export const CONNECTOR_PERMISSION_CHANGED_QUERY_KEYS = [
  [EFFECTIVE_ORG_CONNECTOR_APPROVAL_QUERY_KEY],
  [CONNECTOR_APPROVAL_QUERY_KEY],
  [CONNECTOR_APPROVAL_POLICY_CHANGES_QUERY_KEY],
] as const;

// NOTE: NSAI-4976 — query param carrying the Approval settings card's active
// tab (EConnectorApprovalTab), so the History tab can be linked to directly.
export const CONNECTOR_APPROVAL_TAB_PARAM = "approvalTab";

// NOTE: Approval levels ordered ascending by strictness (loosest → strictest),
// mirroring the server's APPROVAL_LEVEL_STRICTNESS. Used to decide which level
// options fall below an org-imposed floor.
export const CONNECTOR_APPROVAL_LEVEL_STRICTNESS: readonly EConnectorApprovalLevel[] =
  [
    EConnectorApprovalLevel.NEVER_ASK,
    EConnectorApprovalLevel.ASK_ON_IMPORTANT,
    EConnectorApprovalLevel.ASK_ON_CHANGES,
    EConnectorApprovalLevel.ALWAYS_ASK,
  ];

// NOTE: Tool stances ordered ascending by strictness (ALLOW → ASK → BLOCK),
// mirroring the server's STANCE_STRICTNESS.
export const TOOL_STANCE_STRICTNESS: readonly EToolApprovalStance[] = [
  EToolApprovalStance.ALLOW,
  EToolApprovalStance.ASK,
  EToolApprovalStance.BLOCK,
];

// NOTE: Default per-connector level used when a connector has no stored
// setting — kept in sync with the server default (ASK_ON_IMPORTANT).
export const DEFAULT_CONNECTOR_APPROVAL_LEVEL =
  EConnectorApprovalLevel.ASK_ON_IMPORTANT;

// NOTE: Display order of connector approval levels in the settings UI
// (strictest → most permissive). Shared by web and mobile level pickers.
export const CONNECTOR_APPROVAL_LEVEL_ORDER: readonly EConnectorApprovalLevel[] =
  [
    EConnectorApprovalLevel.ALWAYS_ASK,
    EConnectorApprovalLevel.ASK_ON_CHANGES,
    EConnectorApprovalLevel.ASK_ON_IMPORTANT,
    EConnectorApprovalLevel.NEVER_ASK,
  ];

// NOTE: Display order of org minimum approval levels (most permissive →
// strictest). Derived from the shared connector level order so org/web/mobile
// settings stay in sync without duplicating the enum sequence locally.
export const CONNECTOR_APPROVAL_MIN_LEVEL_ORDER: readonly EConnectorApprovalLevel[] =
  [...CONNECTOR_APPROVAL_LEVEL_ORDER].reverse();

// NOTE: Per-action stance options shown in the override control. "follow"
// reverts the action to the connector-level default. Shared by web and mobile.
export const CONNECTOR_ACTION_STANCE_OPTIONS: readonly TActionStanceValue[] = [
  "follow",
  EToolApprovalStance.ALLOW,
  EToolApprovalStance.ASK,
  EToolApprovalStance.BLOCK,
];
